import React, { useEffect, useState } from 'react';
import FormButton from 'components/form-components/FormButton';
import AlertModal from 'components/common-components/AlertModal';
import TextField from 'components/form-components/TextField';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import TextArea from 'components/form-components/TextArea';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import { CallAPI } from 'actions/General';
import { useTranslation } from 'react-i18next';
import { addCompanyFieldSchema, transformDataSources } from './utils';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import NestedSelect from 'components/form-components/NestedSelect';
import Error from 'components/Error';

const CompanyFieldFormModal = ({ handleClose, refreshList, selectedRecordId }) => {
  const { t } = useTranslation();

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dataSources, setDataSources] = useState({
    dataSourceMap: {},
    columnMapByDataSourceId: {},
    dataSourceOptions: [],
    columnDisplayMap: {},
  });

  const { dataSourceMap, columnMapByDataSourceId, dataSourceOptions, columnDisplayMap } =
    dataSources;

  const [values, setValues] = useState({
    fieldTitle: '',
    dbColumn: '',
    dataSource: null,
    dataSourceColumn: '',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [APIError, setAPIError] = useState('');

  const fetchData = async () => {
    setLoadingData(true);
    const result = await CallAPI('GET_ALL_DATA_SOURCES_FOR_FORMS', {}, null, null, setAPIError);
    if (result.status) {
      const transformed = transformDataSources(result.data);
      setDataSources(transformed);
      if (selectedRecordId) {
        let response = await CallAPI(
          'GET_COMPANY_FIELD_DETAIL',
          null,
          selectedRecordId,
          null,
          setAPIError,
        );
        if (response.status) setSelectedRecord(response.data);
      }
    }
    setLoadingData(false);
  };

  const handleSubmit = async () => {
    try {
      setTouched({});
      const payload = {
        fieldTitle: values.fieldTitle.trim(),
        dbColumn: values.dbColumn.trim().toLowerCase(),
        dataSource: values.dataSource,
        dataSourceColumn: values.dataSourceColumn,
        description: values.description,
      };
      await addCompanyFieldSchema.validate(payload, { abortEarly: false });

      const apiName = selectedRecordId ? 'UPDATE_COMPANY_FIELDS' : 'ADD_COMPANY_FIELDS';
      const result = await CallAPI(apiName, payload, selectedRecordId, setLoadingSubmit);

      if (result?.status) {
        toast.success(
          t(
            selectedRecordId
              ? 'alert_success_company_field_updated'
              : 'alert_success_company_field_added',
          ),
        );
        handleClose();
        refreshList();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });
      setErrors(schemaErrors);
    }
  };

  useEffect(() => {
    if (selectedRecord) {
      setValues({
        fieldTitle: selectedRecord.fieldTitle ?? '',
        dbColumn: selectedRecord.dbColumn ?? '',
        dataSource: selectedRecord.dataSource._id ?? '',
        dataSourceColumn: selectedRecord.dataSourceColumn ?? '',
        description: selectedRecord.description ?? '',
      });
    }
  }, [selectedRecord]);

  const handleChange = (field, value) => {
    setValues({
      ...values,
      [field]: value,
      ...(field === 'dataSource' && { dataSourceColumn: null }),
    });
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <AlertModal show={true} actionButtons={[]} noPadding>
        <div className='w-100'>
          <div className='d-flex px-4 py-3 border-bottom-grey-10-h-1'>
            <label className='theme-size-1_5 theme-font-inter-medium theme-text-black-1'>
              {t(selectedRecordId ? 'text_update_company_field' : 'text_add_company_field')}
            </label>
          </div>

          {APIError && <Error msg={APIError} />}

          <div className='px-4 py-3'>
            <TextField
              name='fieldTitle'
              classes='w-auto mb-3'
              value={values.fieldTitle}
              error={errors.fieldTitle}
              touched={touched.fieldTitle}
              handleChange={handleChange}
              placeholder='field_name'
              displayName='field_name'
              shrinkFieldOnly
              disabled={loadingData || loadingSubmit}
            />
            <TextField
              name='dbColumn'
              classes='w-auto mb-3'
              value={values.dbColumn}
              error={errors.dbColumn}
              touched={touched.dbColumn}
              handleChange={handleChange}
              placeholder='field_sql_column'
              displayName='field_sql_column'
              disabled={loadingData || loadingSubmit || !!selectedRecordId}
              shrinkFieldOnly
            />

            <div className='d-flex gap-3'>
              <div className='w-100 user-select-none mb-3'>
                <label className='theme-size-1_0 mb-1'>{t('field_datasource_file')}</label>
                <NestedSelect
                  name='dataSource'
                  options={dataSourceOptions}
                  handleChange={handleChange}
                  toggleClasses='theme-height-44 d-flex justify-content-between align-items-center px-2 py-1 border-grey-1-h-1'
                  toggleComponent={
                    <>
                      <label className='theme-size-0_9 cursor-pointer'>
                        {t(dataSourceMap[values.dataSource] ?? 'select_select')}
                      </label>
                      <img src={chevronGreyIcon} className='rotate-180' />
                    </>
                  }
                  error={errors.dataSource}
                  searchable
                  scrollable
                  fullWidth
                  disabled={loadingData || loadingSubmit}
                />
              </div>
              <div className='w-100 user-select-none mb-3'>
                <label className='theme-size-1_0 mb-1'>{t('field_datasource_column')}</label>
                <NestedSelect
                  name='dataSourceColumn'
                  options={columnMapByDataSourceId[values.dataSource] ?? []}
                  handleChange={handleChange}
                  toggleClasses='theme-height-44 d-flex justify-content-between align-items-center px-2 py-1 border-grey-1-h-1'
                  toggleComponent={
                    <>
                      <label className='theme-size-0_9 cursor-pointer'>
                        {t(
                          columnDisplayMap[values.dataSource]?.[values.dataSourceColumn] ??
                            'select_select',
                        )}
                      </label>
                      <img src={chevronGreyIcon} className='rotate-180' />
                    </>
                  }
                  error={errors.dataSourceColumn}
                  scrollable
                  fullWidth
                  disabled={loadingData || loadingSubmit}
                />
              </div>
            </div>

            <TextArea
              name='description'
              displayName='field_description'
              placeholder='field_description'
              value={values.description}
              handleChange={handleChange}
              classes='w-auto mb-3'
              rows={3}
              disabled={loadingData || loadingSubmit}
            />
          </div>

          <div className='d-flex gap-2 justify-content-end p-3 border-top-grey-10-h-1'>
            <FormButton text={'button_cancel'} variant='white-1' onClick={handleClose} />
            <FormLoadingButton
              text={selectedRecordId ? 'button_update' : 'button_create'}
              variant='green-1'
              loading={loadingSubmit}
              disabled={loadingData}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </AlertModal>
    </div>
  );
};

CompanyFieldFormModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
  selectedRecordId: PropTypes.string,
};

export default CompanyFieldFormModal;
